import React, { ReactNode } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";

interface HrmsTableProps {
  head: ReactNode[];
  /** When true a single centered "no data" row is rendered instead of children. */
  isEmpty?: boolean;
  emptyText?: string;
  children: ReactNode;
}

/**
 * Shared HRMS list table: a white rounded card with a muted header row, row
 * hover and horizontal scroll, so every HRMS list looks the same. Rows are
 * plain <tr>/<td> markup supplied by the caller.
 */
/** Card frame + cell styling shared by every HRMS table. */
const TABLE_SX = {
      "& table": { width: "100%", borderCollapse: "collapse", marginBottom: 0 },
      "& thead th": {
        bg: "#f7fafa",
        color: "#5b6b6b",
        fontSize: "11px",
        fontWeight: 700,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        textAlign: "left",
        whiteSpace: "nowrap",
        px: 3,
        py: 3,
        borderBottom: "1px solid #e2e8f0",
      },
      "& tbody td": {
        fontSize: "13px",
        color: "#2d3b3b",
        px: 3,
        py: 2.5,
        verticalAlign: "middle",
        borderBottom: "1px solid #eef2f2",
      },
      "& tbody tr:last-of-type td": { borderBottom: "none" },
      "& tbody tr:hover td": { bg: "#fafcfc" },
    };

/**
 * Wraps a hand-built <table> in the shared HRMS table styling, for pages that
 * render their own thead/tbody.
 */
export const HrmsTableFrame = ({ children }: { children: ReactNode }) => (
  <Box
    bg="white"
    border="1px solid #e2e8f0"
    borderRadius="12px"
    overflowX="auto"
    sx={TABLE_SX}
  >
    {children}
  </Box>
);

export const HrmsTable = ({
  head,
  isEmpty,
  emptyText = "No data to display",
  children,
}: HrmsTableProps) => (
  <Box
    bg="white"
    border="1px solid #e2e8f0"
    borderRadius="12px"
    overflowX="auto"
    sx={TABLE_SX}
  >
    <table className="hrms-table">
      <thead>
        <tr>
          {head.map((cell, i) => (
            <th key={i}>{cell}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {isEmpty ? (
          <tr>
            <td colSpan={head.length}>
              <Flex direction="column" align="center" gap={2} py={10} color="gray.400">
                <InfoOutlineIcon boxSize={5} />
                <Text fontSize="14px" m={0}>
                  {emptyText}
                </Text>
              </Flex>
            </td>
          </tr>
        ) : (
          children
        )}
      </tbody>
    </table>
  </Box>
);
